import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Grid3X3,
  List,
  User,
  Mail,
  Phone,
  MapPin,
  Star,
  Eye,
  CheckCircle,
  XCircle,
  Award,
  Users,
  Download,
  UserCheck,
  Shield,
  Image as ImageIcon,
  Upload,
  X,
  GraduationCap,
  Lock,
  Briefcase,
  Clock,
  MessageCircle,
  Calendar,
  DollarSign,
  Camera,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../contexts/AuthContext";
import AdminLayout from "../common/AdminLayout";
import TalentGallery from "./TalentGallery";
import Swal from "sweetalert2";
import { API_BASE_URL, BACKEND_BASE_URL, normalizeImageUrl } from "../../utils/api";

interface Category {
  id: number;
  name: string;
  description?: string;
  status: "active" | "inactive";
}

interface Skill {
  id: number;
  name: string;
  category_id: number;
  category_name: string;
  description?: string;
  status: "active" | "inactive";
}

interface TalentSkill {
  skill_id: number;
  name: string;
  category_name: string;
  experience_level: "beginner" | "intermediate" | "expert";
}

interface Talent {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  address?: string;
  parish?: string;
  profile_picture?: string;
  cover_image?: string;
  bio?: string;
  experience_years: number;
  verification_status: "pending" | "verified" | "rejected";
  profile_status: "active" | "inactive" | "suspended";
  rating: number;
  total_reviews: number;
  total_jobs_completed: number;
  created_at: string;
  hourly_rate?: number;
  response_time?: string;
  availability?: string;
  portfolio?: string[];
  education?: Array<{
    degree: string;
    institution: string;
    field_of_study: string;
    graduation_year: number;
    description?: string;
  }>;
  skills?: Array<{
    id: number;
    name: string;
    category_name: string;
    experience_level: "beginner" | "intermediate" | "expert";
  }>;
}

const styles = `
  .upload-button:hover {
    background: var(--background-medium) !important;
    border-color: var(--accent-blue) !important;
  }
  .talent-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 20px;
    margin-bottom: 20px;
    transition: all 0.2s ease;
    border: 1px solid #e5e7eb;
  }
  .talent-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
  .talent-header {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 16px;
  }
  .talent-avatar {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #f3f4f6;
  }
  .talent-avatar-placeholder {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000;
    font-size: 24px;
    font-weight: 600;
    border: 3px solid #f3f4f6;
  }
  .talent-info {
    flex: 1;
  }
  .talent-name {
    font-size: 20px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 4px 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .talent-location {
    color: #6b7280;
    font-size: 14px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .talent-stats {
    display: flex;
    gap: 16px;
    margin-bottom: 12px;
  }
  .talent-stat {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    color: #6b7280;
  }
  .talent-actions {
    display: flex;
    gap: 8px;
    margin-left: auto;
  }
  .talent-bio {
    color: #4b5563;
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 16px;
  }
  .talent-skills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
  }
  .skill-tag {
    background: #eff6ff;
    color: #2563eb;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
  }
  .talent-portfolio {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
    overflow-x: auto;
  }
  .portfolio-image {
    width: 60px;
    height: 60px;
    border-radius: 8px;
    object-fit: cover;
    border: 1px solid #e5e7eb;
  }
  .talent-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 16px;
    border-top: 1px solid #f3f4f6;
  }
  .talent-badges {
    display: flex;
    gap: 8px;
  }
  .talent-rating {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #f59e0b;
    font-weight: 500;
  }
`;

const TalentComponent: React.FC = () => {
  const [talent, setTalent] = useState<Talent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterVerification, setFilterVerification] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [showTalentModal, setShowTalentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingTalent, setEditingTalent] = useState<Talent | null>(null);
  const [selectedTalent, setSelectedTalent] = useState<Talent | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalTalent: 0,
    limit: 10,
  });

  const { admin } = useAuth();

  const fetchTalent = async () => {
    try {
      setLoading(true);
      const response = await api.get('/talent');
      console.log('Talent data:', response.data.talent);
      const manish = response.data.talent.find((t: any) => t.id === 2);

      console.log('Manish data:', manish);

      if (response.data.success) {
        setTalent(response.data.talent || []);
        // set pagination from frontend
        setPagination({
          currentPage,
          totalPages: Math.ceil((response.data.talent?.length || 0) / itemsPerPage),
          totalTalent: response.data.talent?.length || 0,
          limit: itemsPerPage,
        });
      } else {
        setTalent([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch talent:', error);
      setTalent([]);
    } finally {
      setLoading(false);
    }
  };

  // const fetchTalent = async () => {
  //   try {
  //     setLoading(true);

  //     const response = await api.get("/talent", {
  //       params: {
  //         page: currentPage,
  //         limit: itemsPerPage,
  //         search: searchTerm,
  //         status: filterStatus,
  //         verification: filterVerification,
  //       },
  //     });

  //     if (response.data.success) {
  //       setTalent(response.data.talent || []);
  //       setPagination(response.data.pagination);
  //     } else {
  //       setTalent([]);
  //     }
  //   } catch (error) {
  //     console.error("Failed to fetch talent:", error);
  //     setTalent([]);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  useEffect(() => {
    fetchTalent();
  }, [currentPage, filterStatus, filterVerification]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterVerification]);

  const openTalentDetails = (talent: Talent) => {
    setSelectedTalent(talent);
    setShowDetailsModal(true);
  };

  const openEditTalent = (talent: Talent) => {
    setEditingTalent(talent);
    setShowTalentModal(true);
  };

  const handleDeleteTalent = async (id: number) => {
    const result = await Swal.fire({
      title: "Confirm Delete",
      text: "Are you sure you want to delete this talent?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/talent/${id}`);
        fetchTalent();
        Swal.fire({
          title: "Success!",
          text: "Talent deleted successfully",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#28a745",
        });
      } catch (error) {
        console.error("Failed to delete talent:", error);
        Swal.fire({
          title: "Error!",
          text: "Failed to delete talent",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#dc3545",
        });
      }
    }
  };

  const getImageUrl = (imagePath: string | undefined) => {
    return normalizeImageUrl(imagePath, "");
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return "badge-success";
      case "inactive":
        return "badge-warning";
      case "suspended":
        return "badge-danger";
      default:
        return "badge-secondary";
    }
  };

  const getVerificationBadge = (status: string) => {
    switch (status) {
      case "verified":
        return "badge-success";
      case "pending":
        return "badge-warning";
      case "rejected":
        return "badge-danger";
      default:
        return "badge-secondary";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Filter talent
  const filteredTalent = talent.filter(t => {
    const matchesSearch =
      t.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || t.profile_status === filterStatus;
    const matchesVerification = filterVerification === 'all' || t.verification_status === filterVerification;

    return matchesSearch && matchesStatus && matchesVerification;
  });

  const totalPages = Math.ceil(filteredTalent.length / itemsPerPage);
  const currentTalent = filteredTalent.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // const currentTalent = talent;

  const exportToCSV = () => {
    const headers = [
      "First Name",
      "Last Name",
      "Email",
      "Phone Number",
      "Address",
      "Parish",
      "Status",
      "Verification Status",
    ];

    const csvContent = [
      headers.join(","),
      ...currentTalent.map((talent) => [
        talent.first_name,
        talent.last_name,
        talent.email,
        talent.phone,
        talent.address,
        talent.parish,
        talent.profile_status,
        talent.verification_status,
      ].join(",")),
    ].join("\n");

    if (talent.length === 0) {
      Swal.fire({
        title: "No talent found",
        icon: "info",
        confirmButtonText: "OK",
      });
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "talent.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <AdminLayout
        title="Talent Management"
        subtitle="Manage service providers and their profiles"
      >
        <div className="loading-spinner" />
      </AdminLayout>
    );
  }

  const stats = {
    totalTalent: talent.length,
    activeTalent: talent.filter((t) => t.profile_status === "active").length,
    verifiedTalent: talent.filter((t) => t.verification_status === "verified")
      .length,
    avgRating:
      talent.length > 0
        ? (
          talent.reduce(
            (sum, t) =>
              sum +
              (typeof t.rating === "number" && !isNaN(t.rating)
                ? t.rating
                : 0),
            0,
          ) / talent.length
        ).toFixed(1)
        : "0",

  };

  return (
    <AdminLayout
      title="Talent Management"
      subtitle="Manage service providers and their profiles"
    >
      <div className="fade-in">
        <style>{styles}</style>

        {/* Header Actions */}
        <div className="admin-card" style={{ marginBottom: "25px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                flex: 1,
              }}
            >
              <div style={{ position: "relative", minWidth: "300px" }}>
                <input
                  type="text"
                  placeholder="Search talent by name or email..."
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: "40px" }}
                />
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--text-dark-gray)",
                  }}
                />
              </div>

              <select
                className="form-control"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ minWidth: "120px" }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>

              <select
                className="form-control"
                value={filterVerification}
                onChange={(e) => setFilterVerification(e.target.value)}
                style={{ minWidth: "120px" }}
              >
                <option value="all">All Verification</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={exportToCSV} className="btn-secondary">
                <Download size={18} />
                Export
              </button>
              <button
                className="btn-primary"
                onClick={() => setShowTalentModal(true)}
              >
                <Plus size={18} />
                Add Talent
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div
          className="stat-cards start-cat-card"
          style={{ marginBottom: "25px" }}
        >
          <div className="stat-card">
            <div className="card-count">
              <div className="stat-icon">
                <Users size={24} />
              </div>
              <div className="stat-value">{stats.totalTalent}</div>
            </div>
            <div className="card-dec">
              <div className="stat-label">Total Talent</div>
              <div
                style={{
                  fontSize: "14px",
                  color: "var(--accent-blue)",
                  marginTop: "0px",
                }}
              >
                {stats.activeTalent} active
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="card-count">
              <div className="stat-icon">
                <Shield size={24} />
              </div>
              <div className="stat-value">{stats.verifiedTalent}</div>
            </div>
            <div className="card-dec">
              <div className="stat-label">Verified Talent</div>
              <div
                style={{
                  fontSize: "14px",
                  color: "var(--accent-green)",
                  marginTop: "0px",
                }}
              >
                {stats.totalTalent > 0
                  ? ((stats.verifiedTalent / stats.totalTalent) * 100).toFixed(
                    1,
                  )
                  : "0"}
                % verified
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="card-count">
              <div className="stat-icon">
                <Star size={24} />
              </div>
              <div className="stat-value">{stats.avgRating}</div>
            </div>
            <div className="card-dec">
              <div className="stat-label">Average Rating</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="card-count">
              <div className="stat-icon">
                <Award size={24} />
              </div>
              <div className="stat-value">
                {talent.reduce((sum, t) => sum + (t.total_jobs_completed || 0), 0)}
              </div>
            </div>
            <div className="card-dec">
              <div className="stat-label">Total Jobs</div>
            </div>
          </div>
        </div>

        {/* Talent Cards */}
        <div className="admin-card">
          <div className="card-header">
            <div className="card-title">Talent Profiles</div>
            <div style={{ fontSize: "14px", color: "var(--text-medium-gray)" }}>
              Showing {talent.length} of {pagination.totalTalent} talent
            </div>
          </div>

          <div style={{ padding: "20px" }}>
            {currentTalent.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "var(--text-medium-gray)",
                }}
              >
                <Users
                  size={48}
                  style={{ marginBottom: "16px", opacity: 0.5 }}
                />
                <h3>No talent found</h3>
                <p>Try adjusting your search criteria or add new talent.</p>
              </div>
            ) : (
              currentTalent.map((t) => (
                <div key={t.id} className="talent-card">
                  <div className="talent-header">
                    {t.profile_picture ? (
                      <img
                        src={getImageUrl(t.profile_picture)}
                        alt={`${t.first_name} ${t.last_name}`}
                        className="talent-avatar"
                      />
                    ) : (
                      <div className="talent-avatar-placeholder">
                        {t.first_name.charAt(0)}
                        {t.last_name.charAt(0)}
                      </div>
                    )}

                    <div className="talent-info">
                      <h3 className="talent-name">
                        {t.first_name} {t.last_name}
                        {t.verification_status === "verified" && (
                          <CheckCircle size={20} style={{ color: "#10b981" }} />
                        )}
                      </h3>

                      <div className="talent-location">
                        <MapPin size={14} />
                        {t.parish || "Jamaica"}
                      </div>

                      <div className="talent-stats">
                        <div className="talent-stat">
                          <Clock size={14} />
                          {t.experience_years} years exp
                        </div>
                        <div className="talent-stat">
                          <Briefcase size={14} />
                          {t.total_jobs_completed} jobs
                        </div>
                        <div className="talent-stat">
                          <Star size={14} style={{ color: "#f59e0b" }} />
                          {parseFloat(t.rating as any || '0').toFixed(1)}{" "}
                          ({t.total_reviews})
                        </div>

                        {t.hourly_rate && (
                          <div className="talent-stat">
                            <DollarSign size={14} />${t.hourly_rate}/hr
                          </div>



                        )}

                      </div>
                    </div>


                    <div className="talent-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => openTalentDetails(t)}
                        style={{ padding: "8px 12px", fontSize: "14px" }}
                      >
                        <Eye size={16} />
                        View
                      </button>
                      <button
                        className="btn-primary"
                        onClick={() => openEditTalent(t)}
                        style={{ padding: "8px 12px", fontSize: "14px" }}
                      >
                        <Edit size={16} />
                        Edit
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => handleDeleteTalent(t.id)}
                        style={{ padding: "8px 12px", fontSize: "14px" }}
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>

                  {t.bio && (
                    <div className="talent-bio">
                      {t.bio.length > 150
                        ? `${t.bio.substring(0, 150)}...`
                        : t.bio}
                    </div>
                  )}

                  {t.skills && t.skills.length > 0 && (
                    <div className="talent-skills">
                      {t.skills.slice(0, 5).map((skill, index) => (
                        <span key={index} className="skill-tag">
                          {skill.name}
                        </span>
                      ))}
                      {t.skills.length > 5 && (
                        <span
                          className="skill-tag"
                          style={{ background: "#f3f4f6", color: "#6b7280" }}
                        >
                          +{t.skills.length - 5} more
                        </span>
                      )}
                    </div>
                  )}

                  {t.portfolio && t.portfolio.length > 0 && (
                    <div className="talent-portfolio">
                      {t.portfolio.slice(0, 4).map((image, index) => (
                        <img
                          key={index}
                          src={getImageUrl(image)}
                          alt={`Portfolio ${index + 1}`}
                          className="portfolio-image"
                        />
                      ))}
                      {t.portfolio.length > 4 && (
                        <div
                          style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "8px",
                            background: "#f3f4f6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#6b7280",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          +{t.portfolio.length - 4}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="talent-footer">
                    <div className="talent-badges">
                      <span
                        className={`badge ${getStatusBadge(t.profile_status)}`}
                      >
                        {t.profile_status}
                      </span>
                      <span
                        className={`badge ${getVerificationBadge(t.verification_status)}`}
                      >
                        {t.verification_status}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-medium-gray)",
                        }}
                      >
                        Joined {formatDate(t.created_at)}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Mail size={14} style={{ color: "#6b7280" }} />
                        <Phone size={14} style={{ color: "#6b7280" }} />
                        <MessageCircle size={14} style={{ color: "#6b7280" }} />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "20px",
              padding: "20px 0",
            }}
          >
            <div>
              Page {pagination.currentPage} of {pagination.totalPages}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                className="btn-secondary"
                disabled={pagination.currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                Previous
              </button>

              <button
                className="btn-secondary"
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Talent Details Modal */}
        {showDetailsModal && selectedTalent && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              className="admin-card"
              style={{
                width: "95%",
                maxWidth: "800px",
                margin: 0,
                maxHeight: "95vh",
                overflowY: "auto",
              }}
            >
              <div className="card-header">
                <div className="card-title">Talent Details</div>
                <button
                  className="btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                  style={{ padding: "8px", minWidth: "auto" }}
                >
                  <X size={18} />
                </button>
              </div>

              <div style={{ padding: "20px" }}>
                <div
                  style={{ display: "flex", gap: "20px", marginBottom: "20px" }}
                >
                  <div
                    style={{
                      width: "120px",
                      height: "120px",
                      background: selectedTalent.profile_picture
                        ? `url(${getImageUrl(selectedTalent.profile_picture)}) center/cover`
                        : "var(--accent-blue)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "36px",
                      fontWeight: "600",
                    }}
                  >
                    {!selectedTalent.profile_picture &&
                      `${selectedTalent.first_name.charAt(0)}${selectedTalent.last_name.charAt(0)}`}
                  </div>

                  <div style={{ flex: 1 }}>
                    <h2 style={{ marginBottom: "10px" }}>
                      {selectedTalent.first_name} {selectedTalent.last_name}
                    </h2>
                    <div style={{ marginBottom: "15px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          marginBottom: "5px",
                        }}
                      >
                        <Mail size={16} />
                        <span>{selectedTalent.email}</span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          marginBottom: "5px",
                        }}
                      >
                        <Phone size={16} />
                        <span>{selectedTalent.phone}</span>
                      </div>
                      {selectedTalent.address && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <MapPin size={16} />
                          <span>{selectedTalent.address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {selectedTalent.bio && (
                  <div style={{ marginBottom: "20px" }}>
                    <h3 style={{ marginBottom: "10px" }}>About</h3>
                    <p
                      style={{
                        color: "var(--text-medium-gray)",
                        lineHeight: "1.6",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {selectedTalent.bio}
                    </p>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                  }}
                >
                  <button
                    className="btn-secondary"
                    onClick={() => setShowDetailsModal(false)}
                  >
                    Close
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      setShowDetailsModal(false);
                      openEditTalent(selectedTalent);
                    }}
                  >
                    Edit Talent
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Talent Form Modal */}
        {showTalentModal && (
          <TalentFormModal
            isOpen={showTalentModal}
            onClose={() => {
              setShowTalentModal(false);
              setEditingTalent(null);
            }}
            onSubmit={() => {
              setShowTalentModal(false);
              setEditingTalent(null);
              fetchTalent();
            }}
            talent={editingTalent}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// Talent Form Modal Component
interface TalentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  talent?: Talent | null;
}

const TalentFormModal: React.FC<TalentFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  talent,
}) => {
  const [formData, setFormData] = useState<{
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    password: string;
    address: string;
    parish: string;
    bio: string;
    experience_years: number;
    hourly_rate: number;
    profile_status: "active" | "inactive" | "suspended";
    verification_status: "pending" | "verified" | "rejected";
  }>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    address: "",
    parish: "",
    bio: "",
    experience_years: 0,
    hourly_rate: 0,
    profile_status: "active",
    verification_status: "pending",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (talent) {
      setFormData({
        first_name: talent.first_name || "",
        last_name: talent.last_name || "",
        email: talent.email || "",
        phone: talent.phone || "",
        password: talent.password || "",
        address: talent.address || "",
        parish: talent.parish || "",
        bio: talent.bio || "",
        experience_years: talent.experience_years || 0,
        hourly_rate: talent.hourly_rate || 0,
        profile_status: (talent.profile_status || "active") as
          | "active"
          | "inactive"
          | "suspended",
        verification_status: (talent.verification_status || "pending") as
          | "pending"
          | "verified"
          | "rejected",
      });
    } else {
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        password: "",
        address: "",
        parish: "",
        bio: "",
        experience_years: 0,
        hourly_rate: 0,
        profile_status: "active",
        verification_status: "pending",
      });
    }
  }, [talent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (talent) {
        // Update existing talent
        await api.put(`/talent/${talent.id}`, formData);
      } else {
        // Create new talent
        await api.post("/talent", formData);
      }
      onSubmit();
    } catch (error) {
      console.error("Failed to save talent:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to save talent. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "experience_years" || name === "hourly_rate"
          ? Number(value)
          : value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 99999,
      }}
    >
      <div
        className="admin-card"
        style={{
          width: "95%",
          maxWidth: "600px",
          margin: 0,
          maxHeight: "95vh",
          overflowY: "auto",
        }}
      >
        <div className="card-header">
          <div className="card-title">
            {talent ? "Edit Talent" : "Add New Talent"}
          </div>
          <button
            className="btn-secondary"
            onClick={onClose}
            style={{ padding: "8px", minWidth: "auto" }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "20px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                Phone *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
          </div>
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "500",
              }}
            >
              Password {!talent && "*"}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="form-control"
              required={!talent}
              placeholder={talent ? "Leave blank to keep current" : ""}
            />
          </div>
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "500",
              }}
            >
              Address
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "500",
              }}
            >
              Parish
            </label>
            <select
              name="parish"
              value={formData.parish}
              onChange={handleChange}
              className="form-control"
            >
              <option value="">Select Parish</option>
              <option value="Kingston">Kingston</option>
              <option value="St. Andrew">St. Andrew</option>
              <option value="St. Thomas">St. Thomas</option>
              <option value="Portland">Portland</option>
              <option value="St. Mary">St. Mary</option>
              <option value="St. Ann">St. Ann</option>
              <option value="Trelawny">Trelawny</option>
              <option value="St. James">St. James</option>
              <option value="Hanover">Hanover</option>
              <option value="Westmoreland">Westmoreland</option>
              <option value="St. Elizabeth">St. Elizabeth</option>
              <option value="Manchester">Manchester</option>
              <option value="Clarendon">Clarendon</option>
              <option value="St. Catherine">St. Catherine</option>
            </select>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                Experience Years
              </label>
              <input
                type="number"
                name="experience_years"
                value={formData.experience_years}
                onChange={handleChange}
                className="form-control"
                min="0"
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                Hourly Rate ($)
              </label>
              <input
                type="number"
                name="hourly_rate"
                value={formData.hourly_rate}
                onChange={handleChange}
                className="form-control"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                Profile Status
              </label>
              <select
                name="profile_status"
                value={formData.profile_status}
                onChange={handleChange}
                className="form-control"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                Verification Status
              </label>
              <select
                name="verification_status"
                value={formData.verification_status}
                onChange={handleChange}
                className="form-control"
              >
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "500",
              }}
            >
              Bio
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              className="form-control"
              rows={4}
              placeholder="Tell us about yourself..."
            />
          </div>

          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading
                ? "Saving..."
                : talent
                  ? "Update Talent"
                  : "Create Talent"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TalentComponent;
